// src/pages/Dashboard.js - VERSION RICH UI élève
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import axios from "axios";
import { CalendarPlus, Lock, GraduationCap, Clock, Star, Type, Gamepad2, Landmark, BookOpen, HandHeart } from "lucide-react";
import CancellationModal from "../components/CancellationModal";
import LetterLibrary from '../components/learning/LetterLibrary';
import ExercisesHub from '../components/learning/ExercisesHub';
import FivePillars from '../components/learning/islamic/FivePillars';
import SuratesHub from '../components/learning/islamic/SuratesHub';
import PrayerLearning from '../components/learning/islamic/PrayerLearning';
import OnboardingGuide from '../components/OnboardingGuide';
import CarnetProgression from '../components/learning/CarnetProgression';
import BanniereTestPlacement from '../components/learning/placement/BanniereTestPlacement';
import { eleveOnboardingSteps } from '../components/onboardingSteps';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Marcellus&family=DM+Sans:wght@300;400;500;600&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .eleve-dashboard {
    min-height: 100vh;
    background: #f7f4ef;
    font-family: 'DM Sans', sans-serif;
    color: #1a1a1a;
    padding-bottom: 40px;
  }

  /* ── HERO ── */
  .eleve-hero {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    padding: 36px 40px;
    position: relative;
    overflow: hidden;
  }
  .eleve-hero::before {
    content: '';
    position: absolute;
    top: -60px; right: -60px;
    width: 300px; height: 300px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    pointer-events: none;
  }
  .eleve-hero::after {
    content: 'ع';
    position: absolute;
    bottom: -20px; left: 24px;
    font-size: 140px;
    color: rgba(255,255,255,0.05);
    font-family: 'Marcellus', serif;
    pointer-events: none;
    line-height: 1;
  }
  .eleve-hero-inner {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
    position: relative;
    z-index: 1;
  }
  .eleve-hero-left {
    display: flex;
    align-items: center;
    gap: 20px;
  }
  .eleve-avatar {
    width: 72px; height: 72px;
    border-radius: 50%;
    background: rgba(255,255,255,0.2);
    border: 3px solid rgba(255,255,255,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    flex-shrink: 0;
  }
  .eleve-hero-name {
    font-family: 'Marcellus', serif;
    font-size: clamp(20px, 3vw, 28px);
    font-weight: 700;
    color: #fff;
    margin-bottom: 4px;
    line-height: 1.2;
  }
  .eleve-hero-email {
    font-size: 13px;
    color: rgba(255,255,255,0.65);
    margin-bottom: 8px;
  }
  .eleve-niveau-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: rgba(255,255,255,0.15);
    border: 1px solid rgba(255,255,255,0.25);
    color: #fff;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
  }

  /* Stats header */
  .eleve-hero-stats {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }
  .eleve-stat-card {
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 12px;
    padding: 12px 16px;
    text-align: center;
    min-width: 90px;
    cursor: pointer;
    transition: all 0.2s;
  }
  .eleve-stat-card:hover { background: rgba(255,255,255,0.22); transform: translateY(-2px); }
  .eleve-stat-card.credits-ok { background: rgba(34,197,94,0.2); border-color: rgba(34,197,94,0.4); }
  .eleve-stat-card.credits-empty { background: rgba(239,68,68,0.2); border-color: rgba(239,68,68,0.4); }
  .eleve-stat-card.no-sub { cursor: pointer; }
  .eleve-stat-num {
    font-family: 'Marcellus', serif;
    font-size: 22px;
    font-weight: 700;
    color: #fff;
    line-height: 1;
  }
  .eleve-stat-label {
    font-size: 10px;
    color: rgba(255,255,255,0.6);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-top: 3px;
  }
  .eleve-stat-sub {
    font-size: 9px;
    color: rgba(255,255,255,0.5);
    margin-top: 2px;
  }

  /* ── LAYOUT ── */
  .eleve-main {
    max-width: 1200px;
    margin: 0 auto;
    padding: 28px 40px;
  }

  /* ── TABS ── */
  .eleve-tabs {
    display: flex;
    gap: 6px;
    background: #fff;
    border: 1px solid #e8e2d9;
    border-radius: 12px;
    padding: 6px;
    margin-bottom: 24px;
    width: fit-content;
  }
  .eleve-tab {
    padding: 9px 20px;
    border: none;
    border-radius: 8px;
    background: transparent;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #888;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
  }
  .eleve-tab:hover { color: #10b981; }
  .eleve-tab.active { background: #10b981; color: #fff; }

  /* ── SECTION CARD ── */
  .eleve-section {
    background: #fff;
    border-radius: 16px;
    border: 1px solid #e8e2d9;
    padding: 28px;
    margin-bottom: 20px;
  }
  .eleve-section-title {
    font-family: 'Marcellus', serif;
    font-size: 17px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .section-title-line {
    flex: 1;
    height: 1px;
    background: #f0ebe2;
  }

  /* ── ACTIONS RAPIDES ── */
  .actions-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 14px;
  }
  .action-card {
    padding: 20px 14px;
    border-radius: 14px;
    text-align: center;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    border: none;
    font-family: 'DM Sans', sans-serif;
  }
  .action-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.12); }
  .action-card.green { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
  .action-card.green-dim { background: linear-gradient(135deg, #0f766e 0%, #065f46 100%); }
  .action-card.amber { background: linear-gradient(135deg, #cca25f 0%, #a8792f 100%); }
  .action-card.purple { background: linear-gradient(135deg, #34d399 0%, #10b981 100%); }
  .action-card.cyan { background: linear-gradient(135deg, #0d9488 0%, #0f766e 100%); }
  .action-card.teal { background: linear-gradient(135deg, #059669 0%, #047857 100%); }
  .action-card-icon { margin-bottom: 10px; color: #fff; display: flex; justify-content: center; }
  .action-card-label {
    font-size: 13px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 4px;
  }
  .action-card-sub {
    font-size: 11px;
    color: rgba(255,255,255,0.75);
  }

  /* ── COURS CARDS ── */
  .cours-card {
    background: #fff;
    border: 1.5px solid #e8e2d9;
    border-radius: 12px;
    padding: 18px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    transition: box-shadow 0.2s;
  }
  .cours-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.07); }
  .cours-card.confirme { border-left: 4px solid #10b981; }
  .cours-card.en_attente { border-left: 4px solid #f59e0b; }
  .cours-card-left { display: flex; align-items: center; gap: 14px; flex: 1; }
  .cours-avatar {
    width: 44px; height: 44px;
    border-radius: 50%;
    background: linear-gradient(135deg, #10b981, #059669);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 700;
    font-size: 16px;
    flex-shrink: 0;
  }
  .cours-prof-name { font-weight: 700; font-size: 15px; color: #1a1a1a; }
  .cours-details { font-size: 12px; color: #888; margin-top: 3px; }
  .cours-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
  }
  .cours-status-badge.confirme { background: #d1fae5; color: #065f46; }
  .cours-status-badge.en_attente { background: #fef3c7; color: #92400e; }

  .btn-join-cours {
    padding: 9px 18px;
    background: linear-gradient(135deg, #10b981, #059669);
    color: #fff;
    border: none;
    border-radius: 9px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
    box-shadow: 0 3px 10px rgba(16,185,129,0.3);
  }
  .btn-join-cours:hover { transform: translateY(-1px); }
  .btn-cancel-cours {
    padding: 7px 14px;
    background: #fee2e2;
    color: #dc2626;
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
  }
  .btn-cancel-cours:hover { background: #fecaca; }
  .cours-time-badge {
    padding: 4px 10px;
    border-radius: 8px;
    font-size: 11px;
    font-weight: 700;
  }
  .cours-time-badge.today  { background: #d1fae5; color: #065f46; }
  .cours-time-badge.soon   { background: #fef3c7; color: #92400e; }
  .cours-time-badge.future { background: #eff6ff; color: #1d4ed8; }

  /* ── EVALUATION CARDS ── */
  .eval-card {
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 12px;
    padding: 18px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  .eval-prof { font-weight: 700; font-size: 15px; color: #1a1a1a; }
  .eval-date { font-size: 12px; color: #888; margin-top: 3px; }
  .btn-evaluate {
    padding: 9px 18px;
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: #fff;
    border: none;
    border-radius: 9px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    transition: all 0.2s;
    box-shadow: 0 3px 10px rgba(245,158,11,0.3);
  }
  .btn-evaluate:hover { transform: translateY(-1px); }

  /* ── EMPTY ── */
  .eleve-empty {
    text-align: center;
    padding: 40px 20px;
    color: #bbb;
  }
  .eleve-empty-icon { font-size: 40px; margin-bottom: 14px; opacity: 0.35; }
  .eleve-empty h4 {
    font-family: 'Marcellus', serif;
    font-size: 17px;
    color: #10b981;
    margin-bottom: 8px;
  }
  .eleve-empty p { font-size: 13px; }

  /* ── FOOTER ── */
  .eleve-footer {
    background: #fff;
    border: 1px solid #e8e2d9;
    border-radius: 16px;
    padding: 24px 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    flex-wrap: wrap;
  }
  .eleve-footer-tips h4 {
    font-family: 'Marcellus', serif;
    font-size: 15px;
    color: #1a1a1a;
    margin-bottom: 8px;
  }
  .eleve-footer-tips ul {
    padding-left: 18px;
    font-size: 13px;
    color: #888;
    line-height: 1.8;
  }
  .btn-logout {
    padding: 11px 22px;
    background: #fee2e2;
    color: #dc2626;
    border: none;
    border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
  }
  .btn-logout:hover { background: #fecaca; }

  /* ── MY REVIEWS LINK ── */
  .reviews-link-box {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 12px;
    padding: 20px;
    margin-top: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  .reviews-link-box h4 { font-size: 14px; font-weight: 700; color: #065f46; margin-bottom: 4px; }
  .reviews-link-box p { font-size: 12px; color: #047857; }
  .btn-see-reviews {
    padding: 9px 18px;
    background: #10b981;
    color: #fff;
    border: none;
    border-radius: 9px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    text-decoration: none;
    display: inline-block;
    transition: all 0.2s;
    white-space: nowrap;
  }
  .btn-see-reviews:hover { background: #059669; }

  /* ── LOADING ── */
  .eleve-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    gap: 16px;
  }
  .spinner-ring {
    width: 44px; height: 44px;
    border: 3px solid #d1fae5;
    border-top-color: #10b981;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── RESPONSIVE ── */
  @media (max-width: 768px) {
    .eleve-hero { padding: 24px 20px; }
    .eleve-main { padding: 20px; }
    .eleve-hero-inner { flex-direction: column; align-items: flex-start; }
    .actions-grid { grid-template-columns: repeat(2, 1fr); }
    .cours-card { flex-direction: column; align-items: flex-start; }
  }
`;

const getInitials = (nom = '') => {
  const p = nom.trim().split(' ');
  return p.length >= 2 ? (p[0][0] + p[1][0]).toUpperCase() : nom.slice(0,2).toUpperCase() || 'É';
};

const getCountdown = (date) => {
  const d = new Date(date);
  const today = new Date();
  d.setHours(0,0,0,0); today.setHours(0,0,0,0);
  const diff = Math.round((d - today) / 86400000);
  if (diff < 0)  return { label: 'Passé',       cls: 'past' };
  if (diff === 0) return { label: "Aujourd'hui", cls: 'today' };
  if (diff === 1) return { label: 'Demain',      cls: 'soon' };
  return { label: `Dans ${diff}j`, cls: 'future' };
};

// isJitsiAvailable : priorité à start_utc (fourni par backend depuis étape 3)
// Fallback : ancien comportement (heure interprétée dans TZ navigateur)
const isJitsiAvailable = (coursOrDate, heureOptional) => {
  let dt;
  if (coursOrDate && typeof coursOrDate === 'object' && coursOrDate.start_utc) {
    dt = new Date(coursOrDate.start_utc);
  } else if (coursOrDate && typeof coursOrDate === 'object' && coursOrDate.date) {
    // C'est un objet cours mais sans start_utc → fallback
    const h = coursOrDate.heure_debut || coursOrDate.heure || coursOrDate.start_time;
    if (!h) return false;
    dt = new Date(`${coursOrDate.date}T${h}`);
  } else {
    // Ancienne signature (date, heure) — rétrocompat
    dt = new Date(`${coursOrDate}T${heureOptional}`);
  }
  const diff = (dt - new Date()) / 60000;
  return diff <= 15 && diff > -60;
};

const getHeure = (r) => r.heure_debut || r.heure || r.start_time;

// Renvoie un Date pour l'heure de début du cours.
// Priorité à r.start_utc (renvoyé par /reservations/me depuis étape 3).
// Fallback : ancien comportement (interprété dans la TZ du navigateur).
const getCoursStart = (r) => {
  if (r.start_utc) return new Date(r.start_utc);
  const h = getHeure(r);
  if (!h || !r.date) return null;
  return new Date(`${r.date}T${h}`);
};

// Renvoie un Date pour l'heure de fin du cours.
const getCoursEnd = (r) => {
  if (r.end_utc) return new Date(r.end_utc);
  const start = getCoursStart(r);
  if (!start) return null;
  if (r.heure_fin && r.date) return new Date(`${r.date}T${r.heure_fin}`);
  return new Date(start.getTime() + 3600000); // +1h par défaut
};

// Formate l'heure du cours pour affichage dans la TZ de l'élève (navigateur).
// Utilise start_utc si disponible, sinon fallback sur heure brute (heure_debut HH:mm).
const formatHeureEleve = (r) => {
  if (r.start_utc) {
    try {
      return new Date(r.start_utc).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch {}
  }
  const h = getHeure(r);
  return h ? h.substring(0, 5) : '';
};

// Idem pour l'heure de fin.
const formatHeureFinEleve = (r) => {
  if (r.end_utc) {
    try {
      return new Date(r.end_utc).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch {}
  }
  return r.heure_fin ? r.heure_fin.substring(0, 5) : '';
};

export default function Dashboard() {
  const [session, setSession] = useState(null);
  const [token, setToken] = useState(null);
  const [coursToReview, setCoursToReview] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [eleveProfil, setEleveProfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [loadingReservations, setLoadingReservations] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [creditsData, setCreditsData] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);


  // Modals apprentissage
  const [showAlphabet, setShowAlphabet] = useState(false);
  const [showExercises, setShowExercises] = useState(false);
  const [showPillars, setShowPillars] = useState(false);
  const [showSurates, setShowSurates] = useState(false);
  const [showPrayer, setShowPrayer] = useState(false);

  const loadEleveProfile = async (accessToken) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/eleves`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      if (res.data.eleves?.length > 0) {
        setEleveProfil(res.data.eleves[0]);
      } else {
        const { data: { session: s } } = await supabase.auth.getSession();
        const email = s?.user?.email;
        const fullName = s?.user?.user_metadata?.full_name || email?.split('@')[0];
        try {
          const createRes = await axios.post(`${process.env.REACT_APP_API_URL}/eleves`,
            { nom: fullName, email },
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          setEleveProfil(createRes.data.eleve || { nom: fullName, email, niveau_arabe: 'débutant' });
        } catch {
          setEleveProfil({ nom: fullName, email, niveau_arabe: 'débutant' });
        }
      }
    } catch {
      setEleveProfil({ nom: 'Élève', niveau_arabe: 'débutant' });
    }
  };

  const loadCreditsData = async (authToken) => {
    try {
      setCreditsLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/credits/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setCreditsData(res.data.success ? res.data : { has_subscription: false });
    } catch {
      setCreditsData({ has_subscription: false });
    } finally {
      setCreditsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setSession(session);
        setToken(session.access_token);
        await Promise.all([
          loadEleveProfile(session.access_token),
          loadCreditsData(session.access_token)
        ]);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!token) return;
    setLoadingReviews(true);
    axios.get(`${process.env.REACT_APP_API_URL}/reviews/can-review`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (res.data.success) setCoursToReview(res.data.cours_to_review);
    }).catch(() => {}).finally(() => setLoadingReviews(false));
  }, [token]);

  useEffect(() => {
    if (!token) return;
    setLoadingReservations(true);
    axios.get(`${process.env.REACT_APP_API_URL}/booking/reservations/me`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (res.data.success) setReservations(res.data.reservations || []);
    }).catch(() => {}).finally(() => setLoadingReservations(false));
  }, [token]);

  // Polling 30s pour rafraîchir les réservations et voir le lien de cours
  useEffect(() => {
    if (!token) return;
    const isTokenExpired = (t) => {
      try {
        const payload = JSON.parse(atob(t.split('.')[1]));
        return payload.exp * 1000 < Date.now();
      } catch { return true; }
    };
    const interval = setInterval(async () => {
      if (isTokenExpired(token)) {
        const { data: { session } } = await supabase.auth.refreshSession();
        if (!session?.access_token) { clearInterval(interval); return; }
        setToken(session.access_token);
        return;
      }
      axios.get(`${process.env.REACT_APP_API_URL}/booking/reservations/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        if (res.data.success) setReservations(res.data.reservations || []);
      }).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const refreshCredits = () => token && loadCreditsData(token);

  const handleCancelReservation = (cours) => {
    setSelectedReservation(cours);
    setShowCancelModal(true);
  };

  const confirmCancelReservation = async (motif) => {
    if (!selectedReservation) return;
    try {
      const { data: { session: s } } = await supabase.auth.getSession();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/booking/reservations/${selectedReservation.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${s?.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ motif_annulation: motif })
      });
      const data = await res.json();
      if (data.success) {
        setShowCancelModal(false);
        setSelectedReservation(null);
        window.location.reload();
      } else alert(`❌ ${data.error || 'Erreur'}`);
    } catch { alert('❌ Erreur lors de l\'annulation'); }
  };

  // Stats
  const now = new Date();
  const coursConfirmes = reservations.filter(r => {
    if (r.statut !== 'confirmé') return false;
    const fin = getCoursEnd(r);
    if (!fin) return false;
    return fin.getTime() + 900000 > now.getTime();
  });
  const coursEnAttente = reservations.filter(r => {
    if (r.statut !== 'en_attente') return false;
    const start = getCoursStart(r);
    if (!start) return false;
    return start.getTime() > now.getTime();
  });
  const coursTermines = reservations.filter(r => {
    if (r.statut !== 'confirmé') return false;
    const start = getCoursStart(r);
    if (!start) return false;
    return start.getTime() <= now.getTime();
  });

  const stats = {
    coursConfirmes: coursConfirmes.length,
    coursEnAttente: coursEnAttente.length,
    coursTermines: coursTermines.length,
    evaluationsEnAttente: coursToReview.length,
    totalReservations: reservations.length
  };

  const hasCredits = creditsData?.has_subscription && creditsData?.abonnement?.credits_disponibles > 0;

  if (loading) return (
    <div className="eleve-dashboard">
      <style>{styles}</style>
      <div className="eleve-loading">
        <div className="spinner-ring" />
        <span style={{ color: '#999', fontSize: 14 }}>Chargement de votre espace…</span>
      </div>
    </div>
  );

  if (!session) return <p>Veuillez vous connecter</p>;

  return (
    <div className="eleve-dashboard">
      <style>{styles}</style>

      <OnboardingGuide steps={eleveOnboardingSteps} storageKey="eleve-onboarding-completed" />

      {/* HERO */}
      <div className="eleve-hero">
        <div className="eleve-hero-inner">
          <div className="eleve-hero-left">
            <div className="eleve-avatar">🎓</div>
            <div>
              <div className="eleve-hero-name">Bienvenue, {eleveProfil?.nom || 'Élève'} !</div>
              <div className="eleve-hero-email">{session.user.email}</div>
              <span className="eleve-niveau-badge">{eleveProfil?.niveau_arabe || 'Débutant'}</span>
            </div>
          </div>

          <div className="eleve-hero-stats">
            {/* Crédits */}
            {creditsLoading ? (
              <div className="eleve-stat-card">
                <div className="eleve-stat-num">⏳</div>
                <div className="eleve-stat-label">Crédits</div>
              </div>
            ) : !creditsData?.has_subscription ? (
              <div className="eleve-stat-card no-sub" onClick={() => window.location.href = '/abonnement'}>
                <div className="eleve-stat-num">🔒</div>
                <div className="eleve-stat-label">Abonnement</div>
                <div className="eleve-stat-sub">Souscrire</div>
              </div>
            ) : (
              <div
                data-tour="credits"
                className={`eleve-stat-card ${creditsData.abonnement.credits_disponibles > 0 ? 'credits-ok' : 'credits-empty'}`}
                onClick={() => creditsData.abonnement.credits_disponibles === 0 && creditsData.abonnement.credits_par_semaine === 0 ? window.location.href = '/abonnement' : refreshCredits()}
              >
                <div className="eleve-stat-num">{creditsData.abonnement.credits_disponibles}</div>
                <div className="eleve-stat-label">Crédit{creditsData.abonnement.credits_disponibles > 1 ? 's' : ''}</div>
                {creditsData.abonnement.credits_disponibles === 0 && (
                  <div className="eleve-stat-sub">
                    {creditsData.abonnement.credits_par_semaine > 0
                      ? `Recharge ${creditsData.abonnement.jours_jusqu_recharge}j`
                      : 'Passer à un abonnement'}
                  </div>
                )}
              </div>
            )}

            <div className="eleve-stat-card" onClick={() => setActiveTab('cours')}>
              <div className="eleve-stat-num">{stats.coursConfirmes}</div>
              <div className="eleve-stat-label">À venir</div>
            </div>
            <div className="eleve-stat-card" onClick={() => setActiveTab('evaluations')}>
              <div className="eleve-stat-num">{stats.evaluationsEnAttente}</div>
              <div className="eleve-stat-label">À évaluer</div>
            </div>
            <div className="eleve-stat-card">
              <div className="eleve-stat-num">{stats.totalReservations}</div>
              <div className="eleve-stat-label">Total</div>
            </div>
            <div className="eleve-stat-card" onClick={() => setActiveTab('progression')} style={{ cursor: 'pointer' }}>
              <div className="eleve-stat-num">🌱</div>
              <div className="eleve-stat-label">Progression</div>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div className="eleve-main">

        {/* TABS */}
        <div data-tour="tabs" className="eleve-tabs">
          {[
            { key: 'dashboard', label: 'Tableau de bord' },
            { key: 'cours',     label: 'Mes cours' },
            { key: 'evaluations', label: 'Évaluations' },
            { key: 'progression', label: 'Ma progression' },
          ].map(t => (
            <button key={t.key} className={`eleve-tab ${activeTab === t.key ? 'active' : ''}`} onClick={() => setActiveTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── ONGLET DASHBOARD ── */}
        {activeTab === 'dashboard' && (
          <>
            {/* Test de placement — visible dès la première connexion (se cache après le test) */}
            <BanniereTestPlacement token={token} />
            <div className="eleve-section">
              <div className="eleve-section-title">
                Actions rapides
                <div className="section-title-line" />
              </div>
              <div className="actions-grid">
                {/* Réserver */}
                <div
                  data-tour="action-reserver"
                  className={`action-card ${hasCredits ? 'green' : 'green-dim'}`}
                  onClick={() => hasCredits ? window.location.href = '/professeurs' : window.location.href = '/abonnement'}
                >
                  <div className="action-card-icon">{hasCredits ? <CalendarPlus size={28} strokeWidth={1.8} /> : <Lock size={28} strokeWidth={1.8} />}</div>
                  <div className="action-card-label">
                    {hasCredits ? 'Réserver un cours' : creditsData?.has_subscription ? 'Plus de crédits' : 'Abonnement requis'}
                  </div>
                  <div className="action-card-sub">
                    {hasCredits ? 'Trouvez votre professeur' : creditsData?.has_subscription ? `Recharge dans ${creditsData?.abonnement?.jours_jusqu_recharge}j` : 'Choisissez un abonnement'}
                  </div>
                </div>

                {/* Mes cours */}
                <div className={`action-card ${stats.coursConfirmes > 0 ? 'green' : 'green-dim'}`} onClick={() => setActiveTab('cours')}>
                  <div className="action-card-icon"><GraduationCap size={28} strokeWidth={1.8} /></div>
                  <div className="action-card-label">Mes cours ({stats.coursConfirmes})</div>
                  <div className="action-card-sub">Cours confirmés à venir</div>
                </div>

                {/* En attente */}
                {stats.coursEnAttente > 0 && (
                  <div className="action-card amber" onClick={() => setActiveTab('cours')}>
                    <div className="action-card-icon"><Clock size={28} strokeWidth={1.8} /></div>
                    <div className="action-card-label">En attente ({stats.coursEnAttente})</div>
                    <div className="action-card-sub">Confirmation du prof</div>
                  </div>
                )}

                {/* Évaluations */}
                <div className={`action-card ${stats.evaluationsEnAttente > 0 ? 'amber' : 'green-dim'}`} onClick={() => setActiveTab('evaluations')}>
                  <div className="action-card-icon"><Star size={28} strokeWidth={1.8} /></div>
                  <div className="action-card-label">Évaluations ({stats.evaluationsEnAttente})</div>
                  <div className="action-card-sub">Cours à évaluer</div>
                </div>

                {/* Alphabet */}
                <div data-tour="alphabet" className="action-card green" onClick={() => setShowAlphabet(true)}>
                  <div className="action-card-icon"><Type size={28} strokeWidth={1.8} /></div>
                  <div className="action-card-label">Alphabet Arabe</div>
                  <div className="action-card-sub">Apprendre les 28 lettres</div>
                </div>

                {/* Exercices */}
                <div className="action-card teal" onClick={() => setShowExercises(true)}>
                  <div className="action-card-icon"><Gamepad2 size={28} strokeWidth={1.8} /></div>
                  <div className="action-card-label">Exercices</div>
                  <div className="action-card-sub">Apprends en jouant</div>
                </div>

              </div>
            </div>

            {/* ── CORAN & SPIRITUALITÉ (section optionnelle, séparée de la langue) ── */}
            <div className="eleve-section">
              <div className="eleve-section-title">
                Coran &amp; Spiritualité
                <div className="section-title-line" />
              </div>
              <div className="actions-grid">
                {/* 5 Piliers */}
                <div data-tour="islamic" className="action-card purple" onClick={() => setShowPillars(true)}>
                  <div className="action-card-icon"><Landmark size={28} strokeWidth={1.8} /></div>
                  <div className="action-card-label">5 Piliers</div>
                  <div className="action-card-sub">Fondements de l'Islam</div>
                </div>

                {/* Sourates */}
                <div className="action-card cyan" onClick={() => setShowSurates(true)}>
                  <div className="action-card-icon"><BookOpen size={28} strokeWidth={1.8} /></div>
                  <div className="action-card-label">Sourates</div>
                  <div className="action-card-sub">Apprendre le Coran</div>
                </div>

                {/* Prière */}
                <div className="action-card teal" onClick={() => setShowPrayer(true)}>
                  <div className="action-card-icon"><HandHeart size={28} strokeWidth={1.8} /></div>
                  <div className="action-card-label">La Prière</div>
                  <div className="action-card-sub">Apprendre à prier</div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── ONGLET COURS ── */}
        {activeTab === 'cours' && (
          <div className="eleve-section">
            <div className="eleve-section-title">
              📚 Mes cours
              <div className="section-title-line" />
            </div>

            {loadingReservations ? (
              <div className="eleve-loading" style={{ minHeight: 200 }}>
                <div className="spinner-ring" />
              </div>
            ) : coursConfirmes.length === 0 && coursEnAttente.length === 0 ? (
              <div className="eleve-empty">
                <div className="eleve-empty-icon">📅</div>
                <h4>Aucun cours planifié</h4>
                <p>Réservez votre premier cours depuis le tableau de bord.</p>
              </div>
            ) : (
              <>
                {/* Confirmés */}
                {coursConfirmes.length > 0 && (
                  <>
                    <p style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#10b981', marginBottom: 12 }}>
                      ✅ Cours confirmés ({coursConfirmes.length})
                    </p>
                    {coursConfirmes.map(cours => {
                      const heure = getHeure(cours);
                      const videoLink = cours.jitsi_url || `https://www.firstarabic.com/cours/firstarabic-${cours.id}?reservationId=${cours.id}`;
                      const available = isJitsiAvailable(cours);
                      const countdown = getCountdown(cours.date);
                      return (
                        <div key={cours.id} className="cours-card confirme">
                          <div className="cours-card-left">
                            <div className="cours-avatar">{getInitials(cours.profs?.nom || 'P')}</div>
                            <div>
                              <div className="cours-prof-name">👨‍🏫 {cours.profs?.nom || 'Professeur'}</div>
                              <div className="cours-details">
                                📅 {new Date(cours.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                                {heure && ` · 🕒 ${formatHeureEleve(cours)}${cours.heure_fin || cours.end_utc ? ` – ${formatHeureFinEleve(cours)}` : ''}`}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span className={`cours-time-badge ${countdown.cls}`}>{countdown.label}</span>
                            <span className="cours-status-badge confirme">✅ Confirmé</span>
                            {available ? (
                              <a href={videoLink} target="_blank" rel="noopener noreferrer" className="btn-join-cours">
                                🎥 Rejoindre
                              </a>
                            ) : (
                              <span style={{ fontSize: 11, color: '#aaa' }}>Lien disponible 15 min avant</span>
                            )}
                            <button className="btn-cancel-cours" onClick={() => handleCancelReservation(cours)}>Annuler</button>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}

                {/* En attente */}
                {coursEnAttente.length > 0 && (
                  <>
                    <p style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#f59e0b', marginTop: 20, marginBottom: 12 }}>
                      ⏳ En attente de confirmation ({coursEnAttente.length})
                    </p>
                    {coursEnAttente.map(cours => {
                      const heure = getHeure(cours);
                      return (
                        <div key={cours.id} className="cours-card en_attente">
                          <div className="cours-card-left">
                            <div className="cours-avatar" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                              {getInitials(cours.profs?.nom || 'P')}
                            </div>
                            <div>
                              <div className="cours-prof-name">👨‍🏫 {cours.profs?.nom || 'Professeur'}</div>
                              <div className="cours-details">
                                📅 {new Date(cours.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                                {heure && ` · 🕒 ${formatHeureEleve(cours)}`}
                                {cours.message_eleve && ` · 💬 ${cours.message_eleve}`}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="cours-status-badge en_attente">⏳ En attente</span>
                            <button className="btn-cancel-cours" onClick={() => handleCancelReservation(cours)}>Annuler</button>
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* ── ONGLET ÉVALUATIONS ── */}
        {activeTab === 'evaluations' && (
          <div className="eleve-section">
            <div className="eleve-section-title">
              ⭐ Cours à évaluer
              <div className="section-title-line" />
            </div>

            {loadingReviews ? (
              <div className="eleve-loading" style={{ minHeight: 200 }}>
                <div className="spinner-ring" />
              </div>
            ) : coursToReview.length === 0 ? (
              <div className="eleve-empty">
                <div className="eleve-empty-icon">⭐</div>
                <h4>Aucun cours à évaluer</h4>
                <p>Les cours terminés apparaîtront ici pour que vous puissiez les évaluer.</p>
              </div>
            ) : (
              coursToReview.map(cours => (
                <div key={cours.id} className="eval-card">
                  <div>
                    <div className="eval-prof">👨‍🏫 {cours.profs?.nom}</div>
                    <div className="eval-date">
                      📅 {new Date(cours.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                  </div>
                  <Link to={`/review/${cours.id}`} className="btn-evaluate">⭐ Évaluer</Link>
                </div>
              ))
            )}

            <div className="reviews-link-box">
              <div>
                <h4>📝 Mes évaluations publiées</h4>
                <p>Consultez et gérez tous vos avis déjà publiés.</p>
              </div>
              <Link to="/my-reviews" className="btn-see-reviews">👁️ Voir mes évaluations</Link>
            </div>
          </div>
        )}

        {/* FOOTER */}
        <div className="eleve-footer">
          <div className="eleve-footer-tips">
            <h4>Conseils pour progresser</h4>
            <ul>
              <li>Soyez ponctuel pour vos cours</li>
              <li>Préparez vos questions à l'avance</li>
              <li>Pratiquez régulièrement entre les cours</li>
              <li>N'hésitez pas à demander des clarifications</li>
            </ul>
          </div>
          <button className="btn-logout" onClick={async () => { await supabase.auth.signOut(); window.location.href = '/'; }}>
            🚪 Se déconnecter
          </button>
        </div>
      </div>

      {/* Modals */}
      <CancellationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={confirmCancelReservation}
        reservation={selectedReservation}
      />
      {activeTab === 'progression' && (
        <div className="eleve-section">
          <div className="eleve-section-title">
            🌱 Mon carnet de progression
            <div className="section-title-line" />
          </div>
          <BanniereTestPlacement token={token} />
          <CarnetProgression token={token} />
        </div>
      )}

      {showAlphabet && <LetterLibrary onClose={() => setShowAlphabet(false)} client={null} channelName={null} userType="eleve" />}
      {showExercises && <ExercisesHub onClose={() => setShowExercises(false)} />}
      {showPillars && <FivePillars onClose={() => setShowPillars(false)} />}
      {showSurates && <SuratesHub onClose={() => setShowSurates(false)} />}
      {showPrayer && <PrayerLearning onClose={() => setShowPrayer(false)} />}
    </div>
  );
}
